<template>
  <a-modal
    :visible="visible"
    :destroy-on-close="true"
    title="Edit Skill"
    ok-text="Save"
    @ok="() => handleUpdateSkill()"
    @cancel="() => handleClose()"
  >
    <a-form layout="horizontal">
      <SkInput
        v-model="updatedSkill.title"
        v-validate="'required|max:255'"
        :error="$validator.errors.first('title')"
        data-vv-name="title"
        label="Title"
        placeholder="Skill title"
      />

      <SkTextarea
        v-model="updatedSkill.description"
        label="Description"
        placeholder="Skill description"
      />

      <template v-if="baseAcl.isAdmin">
        <div>
          <SkSelect
            v-model="selectedMergeSkillId"
            label="Mark as duplicate of"
            :error="$validator.errors.first('merge_skill_id')"
            :options="mergeOptions"
            placeholder="Select a skill"
            data-vv-name="merge_skill_id"
            display-prop="name"
          />

          <p
            v-if="selectedMergeSkillId"
            style="
              margin-left: auto;
              text-align: right;
              margin-top: -25px;
              margin-bottom: 25px;
              width: 340px;
            "
          >
            This means that
            <b style="color: red">
              {{ skill.title }}
            </b>
            will be removed, and
            <b>{{ mergeOptions.find((it) => it.id === selectedMergeSkillId)?.name }}</b>
            will remain.
          </p>
        </div>
        <a-button
          type="danger"
          style="position: absolute; right: 24px; bottom: 64px"
          :disabled="!selectedMergeSkillId"
          @click="handleMerge"
        >
          Deduplicate
        </a-button>
      </template>
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import { SkillInterface } from "../types";
import useMixin from "@/useMixin";
import { apiGetSkills } from "../_utils/api";
import { useAuthenticationStore } from "@/modules/authentication/_store";
import { BaseAclInterface } from "@/modules/authentication/types";

// Props
const props = defineProps({
  visible: { type: Boolean, default: false },
  skill: { type: Object as () => SkillInterface, default: undefined },
});

// Pinia
const authenticationStore = useAuthenticationStore();
const baseAcl = computed<BaseAclInterface>(() => authenticationStore.baseAcl);

// Emits
const emits = defineEmits(["update-skill", "close", "merge-skill"]);

// Mixins
const { setObject } = useMixin();

// Instances
const instance = getCurrentInstance();
const $validator = instance?.proxy.$validator;

// Data properties
const updatedSkill = ref<any>({
  id: undefined,
  title: undefined,
  description: undefined,
});
const skills = ref<Array<SkillInterface>>([]);
const mergeOptions = ref<Array<{ id: number; name: string }>>([]);
const selectedMergeSkillId = ref<number | null>(null);

// Watchers
watch(
  () => props.visible,
  (newProp, oldProp) => {
    if (newProp && !oldProp) {
      updatedSkill.value = setObject(updatedSkill.value, props.skill);
    }
  }
);

onMounted(async () => {
  const { data: result } = await apiGetSkills();
  skills.value = result.data;

  mergeOptions.value = skills.value
    .filter((s) => s.id !== updatedSkill.value.id)
    .map((s) => ({ id: s.id, name: s.title }));
});

const handleUpdateSkill = (): void => {
  $validator?.validateAll().then((result) => {
    if (result) {
      emits("update-skill", updatedSkill.value);
      emits("close");
    }
  });
};

const handleMerge = async (): Promise<void> => {
  if (!selectedMergeSkillId.value) return;
  emits("merge-skill", selectedMergeSkillId.value, updatedSkill.value.id);
  selectedMergeSkillId.value = null;
  emits("close");
};

const handleClose = () => {
  selectedMergeSkillId.value = null;
  emits("close");
};
</script>
